import { elementType, func, number, oneOfType, string } from "prop-types";
import { forwardRef, useCallback, useMemo, useRef, useState } from "react";

import styled from "styled-components";

import FnrTypography from "FnrTypography";

import FnrSimpleError from "UI/FnrSimpleError";

import useComputedStyle from "Utilities/Hooks/useComputedStyle";
import useWidth from "Utilities/Hooks/useWidth";
import dynOpactity from "Utilities/interpolations/dynOpactity";
import retainInputCursorPos from "Utilities/retainInputCursorPos";

import DefaultIcon from "./Decorators/DefaultIcon";
import DefaultInput from "./Inputs/DefaultInput";
import DefaultLabel from "./Labels/DefaultLabel";

const FnrInput3 = forwardRef(
  /**
   * Flocknote's cannonical input component. All additional props are forwarded to whatever is passed to the Componet prop.
   *
   * @param {object} param0
   * @param {import("react").ElementType} param0.Component - the component that should be used for the input. Defaults to StandardInput.
   * @param {import("react").ElementType} param0.LabelComponent - the component that should be used for the label. Is passed `hasValue`, `label`, `left`, and `inputRef` for props and a ref.
   * @param {import("react").ElementType} param0.PrefixComponent - the component preceeding the editable section of the input. Is passed `icon` and `inputRef` for props and a ref.
   * @param {import("react").ElementType} param0.SuffixComponent - the component proceeding the editable section of the input. Is passed `inputRef` for props and a ref.
   * @param {string} param0.value - the value of the input if controlling the component.
   * @param {function} param0.setValue - a function that takes in a string as the first arg and sets `value` to it.
   * @param {function} param0.onChange - onChange is fired just as if would be natively on the passed in Component
   * @param {string} param0.label - passed to LabelComponent
   * @param {string} param0.icon - passed to IconComponent
   * @param {string} param0.className - here so that we can style the wrapper component. To style the input, passed in a styled component to Component.
   * @param {number} param0.charLimit - the number of charaters the input is allowed to be
   * */
  (
    {
      Component = DefaultInput,
      LabelComponent = DefaultLabel,
      PrefixComponent = DefaultIcon,
      SuffixComponent,
      value: valueArg = "",
      setValue: setValueArg,
      onChange: onChangeArg,
      label,
      icon,
      className,
      charLimit = Infinity,
      ...rest
    },
    passedInref
  ) => {
    const localRef = useRef();
    const ref = passedInref || localRef;
    const prefixRef = useRef();
    const prefixWidth = useWidth(prefixRef);
    const suffixRef = useRef();
    const suffixWidth = useWidth(suffixRef);
    const suffixRight = useComputedStyle(suffixRef, "right");
    const [localValue, localSetValue] = useState(setValueArg ? "" : valueArg);
    const [showLengthWarning, setShowLengthWarning] = useState(false);

    const onChange = useCallback(
      (e) => {
        if (charLimit < e.target.value.length) {
          setShowLengthWarning(true);
          if ((localValue || valueArg).length < charLimit) {
            (setValueArg || localSetValue)(e.target.value.slice(0, charLimit));
          }
          retainInputCursorPos(ref.current, { backOne: true });
          return;
        }
        setShowLengthWarning(false);
        if (onChangeArg) {
          onChangeArg(e);
        }
        (setValueArg || localSetValue)(e.target.value);
      },
      [charLimit, localValue, onChangeArg, ref, setValueArg, valueArg]
    );

    const value = useMemo(() => valueArg || localValue, [valueArg, localValue]);
    const refsArePopulated = typeof prefixWidth === "number";

    return (
      <InputWrapper
        data-cy="InputWrapper"
        $opacity={refsArePopulated ? 1 : 0}
        className={className}
      >
        <LabelComponent
          inputRef={ref}
          label={label}
          hasValue={Boolean(value)}
          left={prefixWidth}
        />
        <PrefixComponent ref={prefixRef} inputRef={ref} icon={icon} />
        {SuffixComponent && <SuffixComponent ref={suffixRef} inputRef={ref} />}
        <Component
          $paddingLeft={prefixWidth || undefined}
          $paddingRight={
            suffixWidth
              ? `calc(${suffixWidth}px + ${suffixRight} + ${suffixRight})`
              : undefined
          }
          ref={ref}
          value={value}
          onChange={onChange}
          {...rest}
        />
        <FnrSimpleError show={showLengthWarning}>
          <FnrTypography color="textWarning">
            Only {charLimit} characters are allowed
          </FnrTypography>
        </FnrSimpleError>
      </InputWrapper>
    );
  }
);

const InputWrapper = styled.div`
  position: relative; /* allows label and icon to move about freely. */
  ${dynOpactity}
  display: flex;
  flex-direction: column;
`;

FnrInput3.propTypes = {
  Component: elementType,
  LabelComponent: elementType,
  PrefixComponent: elementType,
  SuffixComponent: elementType,
  value: oneOfType([string, number]),
  setValue: func,
  onChange: func,
  label: string,
  icon: string,
  className: string,
  charLimit: number,
};

export default FnrInput3;
